import {createSlice} from '@reduxjs/toolkit';

const initialState = {
  jobs: [],
  loading: true,
  error: '',
};

/**
 * Recomputes AR-AP derived fields from a job's technicians, proposals, and parts.
 * Used so AR-AP data stays in sync when a job or its assignments are updated.
 * @param {object} job - Job object (may have technicians, proposals, parts).
 * @param {object} [existing] - Existing job in state (for preserving parts_cost when job.parts not present).
 * @returns {object} Fields to set: technicians_cost, parts_cost, total_cost, profit, invoiced_date?, invoice_number?
 */
function computeArApFields(job, existing) {
  const nte = Number(job.nte) || 0;

  let technicians_cost = 0;
  if (Array.isArray(job.technicians)) {
    technicians_cost = job.technicians.reduce((sum, t) => sum + (Number(t.tech_cost) || 0), 0);
  } else if (existing && existing.technicians_cost !== undefined) {
    technicians_cost = Number(existing.technicians_cost) || 0;
  }

  let parts_cost = 0;
  if (Array.isArray(job.parts)) {
    // Use actual parts list (including empty [] after deletions) so AR-AP updates when parts are added/deleted
    parts_cost = job.parts.reduce((sum, p) => sum + (Number(p.cost) || 0), 0);
  } else if (existing && (existing.parts_cost !== undefined && existing.parts_cost !== null)) {
    parts_cost = Number(existing.parts_cost) || 0;
  }

  const total_cost = technicians_cost + parts_cost;
  const profit = nte - total_cost;

  let invoiced_date = null;
  let invoice_number = null;
  if (Array.isArray(job.proposals)) {
    const invoiced = job.proposals.filter((p) => p.invoiced && p.invoiced_at);
    if (invoiced.length > 0) {
      const sorted = [...invoiced].sort((a, b) => new Date(a.invoiced_at) - new Date(b.invoiced_at));
      invoiced_date = sorted[0].invoiced_at;
      invoice_number = sorted[0].invoice_number ?? null;
    }
  } else if (existing) {
    invoiced_date = existing.invoiced_date ?? null;
    invoice_number = existing.invoice_number ?? null;
  }

  return { technicians_cost, parts_cost, total_cost, profit, invoiced_date, invoice_number };
}

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setJobs(state, action) {
      const jobs = action.payload.jobs || [];
      state.jobs = jobs.map((job) => {
        const arAp = computeArApFields(job, null);
        return {
          ...job,
          technicians_cost: arAp.technicians_cost,
          parts_cost: arAp.parts_cost,
          total_cost: arAp.total_cost,
          profit: arAp.profit,
          invoiced_date: arAp.invoiced_date,
          invoice_number: arAp.invoice_number,
        };
      });
      state.loading = false;
      state.error = action.payload.error || '';
    },
    addJob(state, action) {
      const job = action.payload;
      const arAp = computeArApFields(job, null);
      state.jobs = [
        {
          ...job,
          technicians_cost: arAp.technicians_cost,
          parts_cost: arAp.parts_cost,
          total_cost: arAp.total_cost,
          profit: arAp.profit,
          invoiced_date: arAp.invoiced_date,
          invoice_number: arAp.invoice_number,
        },
        ...state.jobs,
      ];
    },
    updateJob(state, action) {
      const index = state.jobs.findIndex(job => job.id === action.payload.id);
      if (index !== -1) {
        const existing = state.jobs[index];
        const updatedJob = { ...action.payload };

        // If proposals are provided, compute pendingProposals from them
        if (updatedJob.proposals && Array.isArray(updatedJob.proposals)) {
          updatedJob.pendingProposals = updatedJob.proposals.filter(p =>
            p.requested_approval_at !== null || p.requested_unlock_at !== null
          );
        } else {
          updatedJob.pendingProposals = existing.pendingProposals;
        }

        // Recompute AR-AP fields from job data (technicians, proposals, parts) so AR-AP stays up to date
        const arAp = computeArApFields(updatedJob, existing);
        updatedJob.technicians_cost = arAp.technicians_cost;
        updatedJob.parts_cost = arAp.parts_cost;
        updatedJob.total_cost = arAp.total_cost;
        updatedJob.profit = arAp.profit;
        updatedJob.invoiced_date = arAp.invoiced_date;
        updatedJob.invoice_number = arAp.invoice_number;

        state.jobs[index] = updatedJob;
      }
    },
    /**
     * Merges AR-AP API response into existing jobs by id. Only updates jobs that exist in state.
     * Does not add or remove jobs. Fields merged: AR-AP specific + overlapping (client_name, work_order, etc.).
     */
    mergeJobsArAp(state, action) {
      const arApList = action.payload.jobs || [];
      const arApFields = [
        'client_name', 'work_order', 'created_at', 'nte', 'status', 'priority', 'state', 'trade', 'city', 'description',
        'invoiced_date', 'invoice_number', 'technicians_cost', 'parts_cost', 'total_cost', 'profit'
      ];
      for (const arApJob of arApList) {
        const idx = state.jobs.findIndex((j) => j.id === arApJob.id);
        if (idx !== -1) {
          for (const field of arApFields) {
            if (arApJob[field] !== undefined) {
              state.jobs[idx][field] = arApJob[field];
            }
          }
        }
      }
    },
    deleteJob(state, action) {
      state.jobs = state.jobs.filter(job => job.id !== action.payload);
    },
    addJobTechnician(state, action) {
      const { jobId, technician } = action.payload;
      const job = state.jobs.find(j => j.id === jobId);
      if (job) {
        if (!job.technicians) {
          job.technicians = [];
        }
        job.technicians.unshift(technician); // Add to beginning (DESC order)
        const arAp = computeArApFields(job, job);
        job.technicians_cost = arAp.technicians_cost;
        job.parts_cost = arAp.parts_cost;
        job.total_cost = arAp.total_cost;
        job.profit = arAp.profit;
      }
    },
    updateJobTechnician(state, action) {
      const { jobId, technicianId, updatedData } = action.payload;
      const job = state.jobs.find(j => j.id === jobId);
      if (job && job.technicians) {
        const techIndex = job.technicians.findIndex(t => t.id === technicianId);
        if (techIndex !== -1) {
          job.technicians[techIndex] = { ...job.technicians[techIndex], ...updatedData };
        }
        const arAp = computeArApFields(job, job);
        job.technicians_cost = arAp.technicians_cost;
        job.parts_cost = arAp.parts_cost;
        job.total_cost = arAp.total_cost;
        job.profit = arAp.profit;
      }
    },
    deleteJobTechnician(state, action) {
      const { jobId, technicianId } = action.payload;
      const job = state.jobs.find(j => j.id === jobId);
      if (job && job.technicians) {
        job.technicians = job.technicians.filter(t => t.id !== technicianId);
        const arAp = computeArApFields(job, job);
        job.technicians_cost = arAp.technicians_cost;
        job.parts_cost = arAp.parts_cost;
        job.total_cost = arAp.total_cost;
        job.profit = arAp.profit;
      }
    },
    removePendingProposal(state, action) {
      const { jobId, proposalId } = action.payload;
      const job = state.jobs.find(j => j.id === jobId);
      if (job && job.pendingProposals) {
        job.pendingProposals = job.pendingProposals.filter(p => p.id !== proposalId);
      }
    },
    removeJobs(_) {
      return initialState;
    },
  },
});

// Selectors
export const getJobs = (state) => state.jobs;

// Actions
export const {setJobs, removeJobs, addJob, updateJob, deleteJob, mergeJobsArAp, addJobTechnician, updateJobTechnician, deleteJobTechnician, removePendingProposal} = jobsSlice.actions;

export default jobsSlice.reducer;
